'use client';

import { useState } from 'react';
import { Warning, Trash } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface DangerZoneProps {
  scopeLabel: string; // human label for the scope being cleared
  deleting: boolean;
  onDelete: () => Promise<void> | void;
}

const CONFIRM_WORD = 'DELETE';

export function DangerZone({ scopeLabel, deleting, onDelete }: DangerZoneProps) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const confirmed = typed.trim().toUpperCase() === CONFIRM_WORD;

  const handleConfirm = async () => {
    if (!confirmed) return;
    await onDelete();
    setOpen(false);
    setTyped('');
  };

  return (
    <section className="rounded-2xl border border-destructive/30 bg-destructive/[0.03] p-[18px]">
      <div className="flex items-start gap-3">
        <div className="grid size-9 shrink-0 place-items-center rounded-xl bg-destructive/10">
          <Warning weight="duotone" className="size-5 text-destructive" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-extrabold tracking-tight text-destructive">Danger zone</h2>
          <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">
            Permanently delete <span className="font-medium text-foreground/80">all memories</span> in
            the <span className="font-medium text-foreground/80">{scopeLabel}</span> scope. This drops
            the underlying graph (facts, entities, embeddings) and cannot be undone.
          </p>
        </div>
        <Button
          size="sm"
          variant="destructive"
          className="h-8 shrink-0 rounded-full px-3 text-xs font-semibold"
          onClick={() => setOpen(true)}
          disabled={deleting}
        >
          <Trash weight="bold" className="mr-1.5 size-3.5" />
          Delete all
        </Button>
      </div>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setTyped('');
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete all memories in {scopeLabel}?</DialogTitle>
            <DialogDescription>
              This is a verifiable hard-delete: the entire memory graph for this scope is dropped.
              Type <span className="font-mono font-semibold text-foreground">{CONFIRM_WORD}</span> to
              confirm.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={CONFIRM_WORD}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && confirmed) {
                e.preventDefault();
                void handleConfirm();
              }
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" disabled={!confirmed || deleting} onClick={() => void handleConfirm()}>
              {deleting ? 'Deleting…' : 'Delete everything'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
